import { computed } from "mobx";
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import { usePopupStore, useTenantInMarketStore } from "../..";
import useClosable from "../../hooks/useClosable";
import useRecentlyViewed from "../../hooks/useRecentlyViewed";
import { useDetailsShapesOnMap } from "../../hooks/useShapesOnMap";
import DetailsType from "../../interfaces/DetailsType";
import { RecentItemType } from "../../interfaces/IRecentItem";
import { ClosableOrder } from "../../store/ClosableStore";
import commonStyles from '../Common.module.css';
import detailsStyles from '../Details.module.css';
import DetailsPanelWithFitBounds from "../DetailsPanelWithFitBounds";
import IconWithTooltip from "../IconWithTooltip";
import SegmentedButtons from "../SegmentedButtons";
import { TenantInMarketFavoriteCheckbox } from "../favorites/FavoriteCheckbox";
import DealIcon from "../icons/DealIcon";
import DetailsIcon from "../icons/DetailsIcon";
import EditIcon from "../icons/EditIcon";
import LeadIcon from "../icons/LeadIcon";
import TenantInMarketIcon from "../icons/TenantInMarketIcon";
import OnMapSpinner from "../spinners/OnMapSpinner";
import TenantInMarketDetailsDealsTab from "./TenantInMarketDetailsDealsTab";
import TenantInMarketDetailsDetailsTab from "./TenantInMarketDetailsDetailsTab";
import TenantInMarketDetailsLeadsTab from "./TenantInMarketDetailsLeadsTab";

function TenantInMarketDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const tenantInMarketStore = useTenantInMarketStore();
    const popupStore = usePopupStore();
    const tenantInMarket = useMemo(() => computed(() => tenantInMarketStore.tenantsInMarket.find(tim => tim.id.toString() === id)), [id]).get();
    const [tenantInMarketTab, setTenantInMarketTab] = useState(TenantInMarketTab.Details);
    const [dealCount, setDealCount] = useState(0);
    const [leadCount, setLeadCount] = useState(0);
    const segmentedButtons = useMemo(() => [
        {
            value: TenantInMarketTab.Details,
            label: (
                <IconWithTooltip title="Details">
                    <DetailsIcon color="#25479A" />
                </IconWithTooltip>
            ),
        },
        { 
            value: TenantInMarketTab.Deals, 
            label: (
                <IconWithTooltip title="Deals" count={dealCount}>
                    <DealIcon color="#25479A" />
                </IconWithTooltip>
            )
        },
        { 
            value: TenantInMarketTab.Leads, 
            label: (
                <IconWithTooltip title="Leads" count={leadCount}>
                    <LeadIcon color="#25479A" />
                </IconWithTooltip>
            )
        },
    ], [dealCount, leadCount]);
    useDetailsShapesOnMap(tenantInMarket && [tenantInMarket.shape], "TenantInMarketShape");
    useRecentlyViewed(Number(id), RecentItemType.TenantInMarket);

    const close = () => navigate("/");
    useClosable(ClosableOrder.Medium, close);

    useEffect(() => {
        if (tenantInMarket)
            popupStore.openTenantInMarketPopup(tenantInMarket.id);
    }, [tenantInMarket]);

    useEffect(() => {
        return () => {
            popupStore.close();
        }
    }, []);

    if (tenantInMarketStore.isTenantsInMarketLoading.fetching)
        return <OnMapSpinner />;

    if (!tenantInMarket || !tenantInMarket.shape)
        return null;

    return (
        <DetailsPanelWithFitBounds type={DetailsType.TenantInMarketDetails} shape={tenantInMarket.shape} onClose={close} controls={[
            <TenantInMarketFavoriteCheckbox key="favorite" id={Number(id)} className={commonStyles.clickableIcon} />,
            <EditIcon key="edit" onClick={() => navigate(`/tenantsInMarket/${id}/edit`)} />
        ]}>
            <div className={detailsStyles.header}>
                <div className={detailsStyles.headerGroup}>
                    <TenantInMarketIcon color="#25479A" />
                    <div className={detailsStyles.title}>
                        <span className={commonStyles.link} onClick={() => popupStore.openTenantInMarketPopup(tenantInMarket.id)}>{tenantInMarket.description}</span>
                    </div>
                </div>
            </div>
            <SegmentedButtons
                value={tenantInMarketTab}
                onChange={setTenantInMarketTab}
                options={segmentedButtons}
                className={detailsStyles.segmentedButtons}
            />
            <div className={detailsStyles.body}>
                {tenantInMarketTab === TenantInMarketTab.Details && <TenantInMarketDetailsDetailsTab />}
                <TenantInMarketDetailsDealsTab visible={tenantInMarketTab === TenantInMarketTab.Deals} setEntityCount={setDealCount} />
                <TenantInMarketDetailsLeadsTab visible={tenantInMarketTab === TenantInMarketTab.Leads} setEntityCount={setLeadCount} />
            </div>
        </DetailsPanelWithFitBounds>
    );
}

export default observer(TenantInMarketDetails);

enum TenantInMarketTab {
    Details,
    Deals,
    Leads,
}